import { useLayoutEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import type { ColumnsType } from 'antd/es/table';

const isNumericWidth = (width: unknown): width is number =>
  typeof width === 'number' && Number.isFinite(width) && width > 0;

export const toPercentageTableColumns = <RecordType extends object>(
  columns: ColumnsType<RecordType>,
): ColumnsType<RecordType> => {
  const visibleColumns = columns.filter((column) => !column.hidden);

  if (
    visibleColumns.length === 0 ||
    visibleColumns.some((column) => !isNumericWidth(column.width))
  ) {
    return columns;
  }

  const totalWidth = visibleColumns.reduce(
    (sum, column) => sum + (isNumericWidth(column.width) ? column.width : 0),
    0,
  );

  return columns.map((column) =>
    column.hidden || !isNumericWidth(column.width)
      ? column
      : {
          ...column,
          width: `${(column.width / totalWidth) * 100}%`,
        },
  );
};

interface UsePercentageTableColumnsProps<RecordType extends object> {
  columns?: ColumnsType<RecordType>;
  enabled: boolean;
  tableHostRef: RefObject<HTMLDivElement | null>;
}

export const usePercentageTableColumns = <RecordType extends object>({
  columns,
  enabled,
  tableHostRef,
}: UsePercentageTableColumnsProps<RecordType>) => {
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  const percentageColumns = useMemo(
    () => (enabled && columns ? toPercentageTableColumns(columns) : columns),
    [columns, enabled],
  );

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const tableBody =
      tableHostRef.current?.querySelector<HTMLElement>('.ant-table-body');

    if (!tableBody) {
      return;
    }

    const updateScrollbarWidth = () => {
      const nextWidth = Math.max(
        0,
        tableBody.offsetWidth - tableBody.clientWidth,
      );
      setScrollbarWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth,
      );
    };

    updateScrollbarWidth();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(updateScrollbarWidth);
    resizeObserver.observe(tableBody);

    return () => resizeObserver.disconnect();
  }, [enabled, tableHostRef]);

  return { columns: percentageColumns, scrollbarWidth };
};
